import React from 'react';

// `any` so a page typed for its router props (match, history) is accepted;
// the route's props are then read back off the module instead of restated.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loaded = { default: React.ComponentType<any> };
type PropsOf<M extends Loaded> = React.ComponentProps<M['default']>;

export type PreloadableRoute<M extends Loaded> = React.ComponentType<
  PropsOf<M>
> & {
  /** Starts the download if it hasn't started. Resolves with the module. */
  load: () => Promise<M>;
  /** `load` for warm-ups: a failure is swallowed, the route loads normally
   *  (and retries the download) when it renders. */
  preload: () => Promise<void>;
};

/**
 * `React.lazy` with a handle to start the download early.
 *
 * A route is built from one of these, never from an inline `React.lazy`, so
 * `preload` and the component that renders are the same instance. Once the
 * module has loaded, the route renders its component directly: a warmed
 * `React.lazy` still commits the Suspense fallback and holds the content for
 * React's 300 ms reveal throttle (see lazy-preloaded.test.tsx).
 */
export default function lazyWithPreload<M extends Loaded>(
  loader: () => Promise<M>,
): PreloadableRoute<M> {
  let loadedComponent: M['default'] | undefined;
  let pending: Promise<M> | undefined;

  const load = () => {
    pending ??= loader().then(
      (module) => {
        loadedComponent = module.default;
        return module;
      },
      (error) => {
        // Forget the failure so the next attempt downloads again, and give
        // routes mounted from now on a fresh React.lazy: it caches a rejection
        // for good, so the old one could never recover. A route already
        // mounted on the failed one still errors, as a plain React.lazy does.
        pending = undefined;
        Lazy = React.lazy(load);
        throw error;
      },
    );
    return pending;
  };

  const preload = () =>
    load().then(
      () => undefined,
      () => undefined,
    );

  let Lazy = React.lazy(load);

  function Route(props: PropsOf<M>) {
    // Chosen once per mount. Switching from <Lazy> to the loaded component
    // when the module resolves mid-mount would change the element type and
    // remount the route, losing its state.
    const [Component] = React.useState<React.ComponentType<PropsOf<M>>>(
      () => loadedComponent ?? Lazy,
    );
    return <Component {...props} />;
  }

  return Object.assign(Route, { load, preload });
}
